import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FieldGrid, Modal, Switch } from '@skupreme/shared/ui';
import { FormComboBox, FormNumberField } from '@skupreme/shared/ui/form';
import { tooltipTexts } from '@utility/constants/common';
import ItemService from '@utility/services/item';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  buildCartonUpdate,
  CARTON_MEASURES,
  type CartonAliasValues,
  cartonAliasSchema,
  cartonFieldLabels,
  convertCarton,
  convertCartonForm,
  toCartonForm,
} from '../../lib/carton-alias';
import {
  aliasedVariants,
  type CartonAttribute,
  type InventoryItem,
} from '../../lib/inventory';

export type CartonAliasTarget = {
  item: InventoryItem;
  /** Edits the item's `index`-th alias; none adds a new alias. */
  index?: number;
};

const EMPTY = toCartonForm(undefined);

/** Adds a carton alias to an item, or edits one alias's carton set-up. */
export function CartonAliasModal({
  target,
  providerId,
  imperial,
  isManual,
  onClose,
  onSaved,
}: {
  target: CartonAliasTarget | null;
  providerId: string;
  imperial: boolean;
  isManual: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const isEdit = target?.index !== undefined;
  const [formImperial, setFormImperial] = useState(imperial);
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState<CartonAttribute[]>([]);
  /* Cartons on hand of the alias being edited, as it opened. */
  const originalCartons = useRef<number | null>(null);
  const filledFrom = useRef('');

  const schema = useMemo(
    () => cartonAliasSchema(!isEdit && isManual),
    [isEdit, isManual],
  );
  const { control, handleSubmit, reset, getValues, trigger } =
    useForm<CartonAliasValues>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      defaultValues: EMPTY,
    });

  const alias = useWatch({ control, name: 'alias' });
  const grossWeight = useWatch({ control, name: 'carton_gross_weight' });

  /* Net weight is checked against gross whenever gross changes. */
  useEffect(() => {
    if (getValues('carton_net_weight') !== null) {
      void trigger('carton_net_weight');
    }
  }, [grossWeight, getValues, trigger]);

  useEffect(() => {
    if (!target) return;

    const variants = aliasedVariants(target.item).map(variant => ({
      alias: variant.alias,
      attribute: convertCarton(
        {
          ...variant.attribute,
          alias: variant.attribute?.alias || variant.alias,
        },
        imperial,
      ),
      cartons:
        (variant.available || variant.available_qty || 0) /
        (variant.attribute?.units_per_carton || 1),
    }));

    setFormImperial(imperial);
    setSaving(false);
    filledFrom.current = '';

    if (target.index !== undefined) {
      const current = variants[target.index];
      originalCartons.current = current?.cartons ?? null;
      reset(toCartonForm(current?.attribute));
      return;
    }

    reset(EMPTY);
    setSuggestions([]);
    let isCurrent = true;

    ItemService.getItemAttributeByUpc(target.item.upc)
      .then(({ data }: { data?: { attributes?: CartonAttribute[] } }) => {
        if (!isCurrent) return;
        setSuggestions(
          (data?.attributes ?? []).filter(
            attribute => !variants.some(x => x.alias === attribute.alias),
          ),
        );
      })
      .catch(error => ToastMessage.apiError(error));

    return () => {
      isCurrent = false;
    };
  }, [target, imperial, reset]);

  /* Picking an alias the item already has elsewhere brings its carton along. */
  useEffect(() => {
    if (isEdit || !alias || alias === filledFrom.current) return;

    const match = suggestions.find(x => x.alias === alias);
    if (!match) return;

    filledFrom.current = alias;
    reset({
      ...toCartonForm(convertCarton(match, formImperial)),
      alias,
      available_qty: null,
    });
  }, [alias, suggestions, isEdit, formImperial, reset]);

  const changeUnits = (next: boolean) => {
    reset(convertCartonForm(getValues(), next));
    setFormImperial(next);
  };

  const save = handleSubmit(async values => {
    if (!target) return;

    let availableCartons: number | null | undefined;
    if (!isEdit) availableCartons = values.available_qty ?? 0;
    else if (isManual) availableCartons = originalCartons.current;

    try {
      setSaving(true);
      await StorageProviderService.updateInventory(
        target.item.fulfillment_provider_id || providerId,
        buildCartonUpdate(values, {
          itemId: target.item.item_id,
          imperial: formImperial,
          availableCartons,
        }),
      );
      ToastMessage.success(
        isEdit
          ? 'Item variants updated successfully.'
          : 'Item Variants added successfully.',
      );
      await onSaved();
      onClose();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setSaving(false);
    }
  });

  const labels = cartonFieldLabels(formImperial);
  const aliasOptions = useMemo(
    () =>
      suggestions
        .filter(x => x.alias)
        .map(x => ({ value: x.alias as string, label: x.alias as string })),
    [suggestions],
  );

  return (
    <Modal
      isOpen={!!target}
      onOpenChange={isOpen => !isOpen && onClose()}
      isDismissable={!saving}
      size="lg"
      title={isEdit ? `Edit ${alias || 'Carton'}` : 'Create New Alias'}
      footer={
        <>
          <Button variant="ghost" onPress={onClose} isDisabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onPress={() => save()} isPending={saving}>
            {isEdit ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <form
        noValidate
        autoComplete="off"
        onSubmit={save}
        className="sk:flex sk:flex-col sk:gap-4"
      >
        <div className="sk:flex sk:items-end sk:gap-4">
          {/* An alias is renamed nowhere: editing keeps it as it is. */}
          {isEdit ? null : (
            <FormComboBox
              control={control}
              name="alias"
              label="Carton Alias Name"
              hint={tooltipTexts.aliasName}
              placeholder="Pick or type an alias"
              options={aliasOptions}
              isRequired
              className="sk:flex-1"
            />
          )}
          <Switch
            isSelected={formImperial}
            onChange={changeUnits}
            className="sk:mb-1.5 sk:ml-auto"
          >
            Imperial
          </Switch>
        </div>
        <FieldGrid columns={isEdit ? 3 : 2}>
          <FormNumberField
            control={control}
            name="units_per_carton"
            label="Units Per Carton"
            hint={tooltipTexts.unitsPerCarton}
            isRequired
          />
          {isEdit ? null : (
            <FormNumberField
              control={control}
              name="available_qty"
              label="Available Cartons"
              isDisabled={!isManual}
              isRequired={isManual}
            />
          )}
          {CARTON_MEASURES.filter(key => key.endsWith('weight')).map(key => (
            <FormNumberField
              key={key}
              control={control}
              name={key}
              label={labels[key].label}
              hint={labels[key].hint}
              isRequired
            />
          ))}
        </FieldGrid>
        <FieldGrid columns={3}>
          {CARTON_MEASURES.filter(key => !key.endsWith('weight')).map(key => (
            <FormNumberField
              key={key}
              control={control}
              name={key}
              label={labels[key].label}
              hint={labels[key].hint}
              isRequired
            />
          ))}
        </FieldGrid>
      </form>
    </Modal>
  );
}
